import { DefaultOptionType } from 'antd/lib/cascader';
import { Property, Schema } from 'hooks/projects';
import { v4 as uuidv4 } from 'uuid';

/**
 * Helper to find schema by type name
 */
export const findSchemaByType = (
  typeName: string,
  schemas: Schema[]
): Schema | undefined => {
  return schemas.find((schema) => schema.name === typeName);
};

/**
 * Extract base type name from GraphQL type
 * @example extractTypeName("[Asset!]!") => "Asset"
 */
export const extractTypeName = (type: string): string => {
  return type.replace(/[\[\]!]/g, '');
};

/**
 * Check if a type is a scalar type (primitive)
 */
export const isScalarType = (typeName: string): boolean => {
  return ['String', 'Int', 'Float', 'Boolean', 'ID', 'DateTime'].includes(
    typeName
  );
};

/**
 * Map properties to cascader options with nested children
 * This allows hierarchical property selection like "equipment.location.name"
 *
 * @param properties - Array of properties to map
 * @param schemas - All available schemas for resolving nested types
 * @param depth - Current nesting depth (used internally)
 * @param maxDepth - Maximum nesting depth to prevent infinite loops
 * @returns Cascader options with nested children
 */
export const mapPropertiesToCascaderOptions = (
  properties: Property[],
  schemas: Schema[],
  depth: number = 0,
  maxDepth: number = 5
): DefaultOptionType[] => {
  // Prevent infinite loops from circular references (e.g., Asset -> Location -> Asset)
  if (depth >= maxDepth) return [];

  return properties.map((prop) => {
    const baseTypeName = extractTypeName(prop.type);
    const isScalar = isScalarType(baseTypeName);

    let children: DefaultOptionType[] | undefined;

    // If it's not a scalar type, try to find the schema and add its properties as children
    if (!isScalar) {
      const relatedSchema = findSchemaByType(baseTypeName, schemas);
      if (relatedSchema && relatedSchema.properties) {
        children = mapPropertiesToCascaderOptions(
          relatedSchema.properties,
          schemas,
          depth + 1,
          maxDepth
        );
      }
    }

    return {
      label: prop.name,
      value: prop.name,
      type: prop.type,
      children: children && children.length > 0 ? children : undefined,
    };
  });
};

/**
 * Check whether a filter operator requires a value input.
 * Null-check operators (isNull, isNotNull) do not need a value.
 */
export const needsValueInput = (operator: string): boolean => {
  return !['isNull', 'isNotNull'].includes(operator);
};

/**
 * Filter condition interface
 */
export interface FilterCondition {
  id: string;
  attribute: string;
  operator: string;
  value: string;
}

/**
 * Create a new empty filter condition with a unique ID.
 */
export const createFilter = (): FilterCondition => ({
  id: `filter-${uuidv4()}`,
  attribute: '',
  operator: 'eq',
  value: '',
});

/**
 * Add a new empty filter to the list.
 * If there are already filters, appends an 'and' operator.
 */
export const addFilter = (
  filters: FilterCondition[],
  operators: ('and' | 'or')[]
): { filters: FilterCondition[]; operators: ('and' | 'or')[] } => {
  const newFilter = createFilter();
  const newOperators: ('and' | 'or')[] =
    filters.length > 0 ? [...operators, 'and'] : [];
  return { filters: [...filters, newFilter], operators: newOperators };
};

/**
 * Update a filter at the given index with partial changes.
 */
export const updateFilter = (
  filters: FilterCondition[],
  index: number,
  updates: Partial<FilterCondition>
): FilterCondition[] => {
  return filters.map((f, i) => (i === index ? { ...f, ...updates } : f));
};

/**
 * Remove a filter at the given index and adjust the operators array.
 */
export const removeFilter = (
  filters: FilterCondition[],
  operators: ('and' | 'or')[],
  index: number
): { filters: FilterCondition[]; operators: ('and' | 'or')[] } => {
  const newFilters = filters.filter((_, i) => i !== index);
  const newOperators = operators.filter((_, i) =>
    index === 0 ? i !== 0 : i !== index - 1
  );
  return { filters: newFilters, operators: newOperators };
};

/**
 * Update the logical operator at the given index.
 */
export const updateOperator = (
  operators: ('and' | 'or')[],
  index: number,
  operator: 'and' | 'or'
): ('and' | 'or')[] => {
  return operators.map((op, i) => (i === index ? operator : op));
};

/**
 * Convert visual filter conditions to JSON format for backend
 * Supports mixed AND/OR operators between conditions
 *
 * @param filterConditions - Array of filter conditions
 * @param operators - Array of operators between conditions (length = conditions.length - 1)
 * @returns JSON string representation of filters
 */
export const convertFiltersToJSON = (
  filterConditions: FilterCondition[],
  operators: ('and' | 'or')[]
): string => {
  if (filterConditions.length === 0) return '{}';

  if (filterConditions.length === 1) {
    const filter = filterConditions[0];
    return JSON.stringify(
      {
        [filter.attribute]: { [filter.operator]: filter.value },
      },
      null,
      2
    );
  }

  // Build nested structure for mixed operators
  // Start with the first condition
  let result: any = {
    [filterConditions[0].attribute]: {
      [filterConditions[0].operator]: filterConditions[0].value,
    },
  };

  // Process each subsequent condition with its operator
  for (let i = 1; i < filterConditions.length; i++) {
    const condition = filterConditions[i];
    const operator = operators[i - 1] || 'and';
    const conditionObj = {
      [condition.attribute]: { [condition.operator]: condition.value },
    };

    // Wrap in operator
    result = {
      [operator]: [result, conditionObj],
    };
  }

  return JSON.stringify(result, null, 2);
};

/**
 * Parse JSON filter string to visual filter conditions
 * This is useful for editing existing filters
 * Recursively handles nested structures created by convertFiltersToJSON
 *
 * @param filterJSON - JSON string of filters
 * @returns Object with filter conditions and operators array
 */
export const parseFiltersFromJSON = (
  filterJSON: string
): { conditions: FilterCondition[]; operators: ('and' | 'or')[] } => {
  if (!filterJSON || filterJSON === '{}') {
    return { conditions: [], operators: [] };
  }

  try {
    const parsed = JSON.parse(filterJSON);
    const result = parseFilterObject(parsed);
    return result;
  } catch (error) {
    return { conditions: [], operators: [] };
  }
};

/**
 * Recursively parse a filter object into conditions and operators
 * Handles nested structures like {"or": [{"and": [C1, C2]}, C3]}
 */
function parseFilterObject(obj: any): {
  conditions: FilterCondition[];
  operators: ('and' | 'or')[];
} {
  if (!obj || typeof obj !== 'object') {
    return { conditions: [], operators: [] };
  }

  const keys = Object.keys(obj);

  // Base case: Single condition (e.g., { "name": { "eq": "value" } })
  if (keys.length === 1 && !['and', 'or'].includes(keys[0])) {
    const attribute = keys[0];
    const operatorObj = obj[attribute];

    if (!operatorObj || typeof operatorObj !== 'object') {
      return { conditions: [], operators: [] };
    }

    const operatorKeys = Object.keys(operatorObj);
    if (operatorKeys.length === 0) {
      return { conditions: [], operators: [] };
    }

    const operator = operatorKeys[0];
    const value = operatorObj[operator];

    return {
      conditions: [
        {
          id: uuidv4(),
          attribute,
          operator,
          value: String(value ?? ''),
        },
      ],
      operators: [],
    };
  }

  // Recursive case: Logical operator with nested conditions
  const logicalOp = keys[0] as 'and' | 'or';
  if (['and', 'or'].includes(logicalOp)) {
    const items = obj[logicalOp];

    if (!Array.isArray(items) || items.length === 0) {
      return { conditions: [], operators: [] };
    }

    // Parse first item
    let result = parseFilterObject(items[0]);

    // Parse remaining items and build operators array
    for (let i = 1; i < items.length; i++) {
      const itemResult = parseFilterObject(items[i]);

      // Add the logical operator before appending conditions
      result.operators.push(logicalOp);

      // Merge conditions
      result.conditions = [...result.conditions, ...itemResult.conditions];

      // Merge operators (itemResult may have its own operators)
      result.operators = [...result.operators, ...itemResult.operators];
    }

    return result;
  }

  return { conditions: [], operators: [] };
}
